import { Cancel, CheckCircle } from "@mui/icons-material";
import {
  Box,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTranslate } from "react-admin";

import {
  LearningProgressCaregiver,
  LearningProgressCourse,
  LearningProgressEntry,
  LearningProgressStatus,
} from "../../hooks/useLearningProgress";
import { borderRadius, color } from "../../theme/designTokens";

type ProgressPrimaryColumn = "caregiver" | "course";

type LearningProgressTableProps = {
  entries: LearningProgressEntry[];
  caregivers: LearningProgressCaregiver[];
  courses: LearningProgressCourse[];
  primaryColumn: ProgressPrimaryColumn;
};

type Summary = {
  assigned: number;
  completed: number;
  completionRate: number;
  averageScore: number | null;
};

const statusColor: Record<LearningProgressStatus, "default" | "warning" | "success"> = {
  NOT_STARTED: "default",
  IN_PROGRESS: "warning",
  COMPLETED: "success",
};

export const formatLearningPercent = (value: number | null): string =>
  value === null ? "—" : `${new Intl.NumberFormat("de-DE", { maximumFractionDigits: 1 }).format(value)} %`;

const formatDate = (value: string | null): string => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString("de-DE");
};

export const summarizeLearningProgress = (entries: LearningProgressEntry[]): Summary => {
  const completed = entries.filter(entry => entry.status === "COMPLETED").length;
  const scores = entries.flatMap(entry => (entry.bestScorePercent === null ? [] : [entry.bestScorePercent]));
  return {
    assigned: entries.length,
    completed,
    completionRate: entries.length === 0 ? 0 : (completed / entries.length) * 100,
    averageScore: scores.length === 0 ? null : scores.reduce((total, score) => total + score, 0) / scores.length,
  };
};

export const LearningStatusChip = ({ status }: { status: LearningProgressStatus }) => {
  const translate = useTranslate();
  return (
    <Chip
      size="small"
      color={statusColor[status]}
      label={translate(`learningProgress.status.${status.toLowerCase()}`)}
    />
  );
};

const QuizHistory = ({ entry }: { entry: LearningProgressEntry }) => {
  const translate = useTranslate();
  if (entry.quizAttempts.length === 0) return <Typography color="text.secondary">—</Typography>;
  return (
    <Stack direction="row" useFlexGap flexWrap="wrap" gap={0.75} mt={0.5}>
      {entry.quizAttempts.map(attempt => {
        const date = formatDate(attempt.finishedAt);
        const score = formatLearningPercent(attempt.serverScorePercent);
        return (
          <Chip
            key={attempt.id}
            size="small"
            variant="outlined"
            color={attempt.serverPassed ? "success" : "error"}
            icon={attempt.serverPassed ? <CheckCircle /> : <Cancel />}
            label={`${score} · ${date}`}
            aria-label={translate("learningProgress.attemptLabel", {
              score,
              date,
              result: translate(
                attempt.serverPassed ? "learningProgress.attemptPassed" : "learningProgress.attemptFailed"
              ),
            })}
          />
        );
      })}
    </Stack>
  );
};

export const LearningProgressSummaryTiles = ({ entries }: { entries: LearningProgressEntry[] }) => {
  const translate = useTranslate();
  const summary = summarizeLearningProgress(entries);
  const tiles = [
    { label: translate("learningProgress.summary.assigned"), value: String(summary.assigned) },
    {
      label: translate("learningProgress.summary.completed"),
      value: `${summary.completed} · ${formatLearningPercent(summary.completionRate)}`,
    },
    { label: translate("learningProgress.summary.averageScore"), value: formatLearningPercent(summary.averageScore) },
  ];
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: 1.5 }}>
      {tiles.map(tile => (
        <Paper
          key={tile.label}
          variant="outlined"
          sx={{ p: 2, borderRadius: borderRadius.sm, borderColor: color.panelBorder }}
        >
          <Typography variant="body2" color="text.secondary">
            {tile.label}
          </Typography>
          <Typography variant="h5" component="p" sx={{ fontWeight: 600 }}>
            {tile.value}
          </Typography>
        </Paper>
      ))}
    </Box>
  );
};

export const LearningProgressTable = ({ entries, caregivers, courses, primaryColumn }: LearningProgressTableProps) => {
  const translate = useTranslate();
  const caregiversById = new Map(caregivers.map(caregiver => [caregiver.userId, caregiver]));
  const coursesById = new Map(courses.map(course => [course.id, course]));
  const showLocations = primaryColumn === "caregiver";

  return (
    <TableContainer>
      <Table size="small" aria-label={translate("learningProgress.tableLabel")}>
        <TableHead>
          <TableRow>
            <TableCell>
              {translate(
                primaryColumn === "caregiver" ? "learningProgress.columns.caregiver" : "learningProgress.columns.course"
              )}
            </TableCell>
            {showLocations && <TableCell>{translate("learningProgress.columns.location")}</TableCell>}
            <TableCell>{translate("learningProgress.columns.status")}</TableCell>
            <TableCell>{translate("learningProgress.columns.score")}</TableCell>
            <TableCell>{translate("learningProgress.columns.completedAt")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {entries.map(entry => {
            const caregiver = caregiversById.get(entry.userId);
            const course = coursesById.get(entry.courseId);
            return (
              <TableRow key={entry.enrollmentId} hover data-testid={`learning-progress-${entry.enrollmentId}`}>
                <TableCell sx={{ fontWeight: 600 }}>
                  {primaryColumn === "caregiver"
                    ? (caregiver?.displayName ?? translate("learningProgress.unknownCaregiver"))
                    : (course?.title ?? translate("learningProgress.unavailableCourse"))}
                </TableCell>
                {showLocations && <TableCell>{caregiver?.locationNames.join(", ") || "—"}</TableCell>}
                <TableCell>
                  <LearningStatusChip status={entry.status} />
                </TableCell>
                <TableCell>
                  <Typography sx={{ fontWeight: 600 }}>{formatLearningPercent(entry.bestScorePercent)}</Typography>
                  <QuizHistory entry={entry} />
                </TableCell>
                <TableCell>{formatDate(entry.completedAt)}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
